import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Notification } from '@shared';
import { BehaviorSubject, interval, map, Observable, shareReplay, Subscription } from 'rxjs';
import * as CryptoJS from 'crypto-js';

export interface State {
  state: string;
  alias: string;
  lgas: string[];
  label?: string;
  value?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  // Validations
  get phoneNumberValidationRegex() {
    return /^\d{10}$/;
  }
  get phoneNumberValidationRegex2() {
    return /^\d{11}$/;
  }
  get emailValidationRegex() {
    return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  }

  // Encryption/Decryption
  private encryptionKey = 'v2y/F?E(H+MbQegg';
  private initializationVector = 's4j/J?T$C+GQrEkl';

  // Timer for countdowns
  private timerSub: Subscription | null = null;
  private countdown$ = new BehaviorSubject<string>('--:--');

  // Local JSON file for states and LGAs
  localStatesUrl: string = 'files/nigeria-state-and-lgas.json';

  // DI Injected services
  private notification = inject(Notification);
  private http = inject(HttpClient);

  private states$: Observable<State[]> | null = null;

  getStates$(): Observable<State[]> {
    if (!this.states$) {
      this.states$ = this.http.get<State[]>(this.localStatesUrl).pipe(
        map((list: State[]) =>
          [...list]
            .sort((a, b) => a.state.localeCompare(b.state))
            .map((state) => ({
              ...state,
              label: state.state,
              value: state.alias,
              lgas: [...state.lgas].sort((a, b) => a.localeCompare(b)),
            })),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
    return this.states$;
  }

  validateAllFormFields(formGroup: FormGroup) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}
      const control = formGroup.get(field); //{3}
      if (control instanceof FormControl) {
        //{4}
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        //{5}
        this.validateAllFormFields(control); //{6}
      } else if (control instanceof FormArray) {
        // control.markAllAsTouched()
        for (let index = 0; index < control.controls.length; index++) {
          const element = control.controls[index];
          element.markAsTouched();
          if (element instanceof FormControl) {
            //{4}
            element.markAsTouched({ onlySelf: true });
          } else if (element instanceof FormGroup) {
            //{5}
            this.validateAllFormFields(element); //{6}
          }
        }
      }
    });
  }

  exportToExcel(blob: Blob, fileName = 'download') {
    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    downloadLink.setAttribute('href', url);
    downloadLink.setAttribute('download', fileName);
    downloadLink.style.visibility = 'hidden';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(url);
  }

  passwordMatchValidator(
    passwordControl: FormControl,
    confirmPasswordControl: FormControl,
  ): ValidatorFn {
    return (): ValidationErrors | null => {
      const password = passwordControl?.value;
      const confirmPassword = confirmPasswordControl?.value;

      if (!confirmPassword || password === confirmPassword) {
        this.clearControlError(confirmPasswordControl, 'passwordMismatch');
        return null;
      }

      confirmPasswordControl.setErrors({
        ...(confirmPasswordControl.errors ?? {}),
        passwordMismatch: true,
      });
      return { passwordMismatch: true };
    };
  }

  private clearControlError(
    control: FormControl | null | undefined,
    errorKey: string,
  ): void {
    if (!control?.errors?.[errorKey]) {
      return;
    }

    const remainingErrors = { ...control.errors };
    delete remainingErrors[errorKey];
    control.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null,
    );
  }

  getCountdown() {
    return this.countdown$.asObservable();
  }

  startCountdown(seconds: number) {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    this.timerSub = interval(1000).subscribe(() => {
      if (seconds <= 0) {
        this.countdown$.next('00:00');
        this.timerSub?.unsubscribe();
      } else {
        seconds--;
        const min = Math.floor(seconds / 60)
          .toString()
          .padStart(2, '0');
        const sec = (seconds % 60).toString().padStart(2, '0');
        this.countdown$.next(`${min}:${sec}`);
      }
    });
    return this.countdown$.asObservable();
  }

  stopCountdown() {
    this.timerSub?.unsubscribe();
    this.timerSub = null;
    this.countdown$.next('--:--');
  }

  socialMediaShare(title: string, text: string) {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: text,
      });
    } else {
      this.notification.info('Sharing is not supported on this device');
    }
  }

  async copyToClipBoard(text: string | number) {
    // Modern Clipboard API (secure context)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text.toString() || '');
        this.notification.info('Copied to clipboard');
        return;
      } catch {
        // fallback below
      }
    }
  }

  encryptObject(object: unknown): string {
    const key = CryptoJS.enc.Utf8.parse(this.encryptionKey);
    const iv = CryptoJS.enc.Utf8.parse(this.initializationVector);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(object), key, {
      keySize: 128 / 8,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.ciphertext.toString();
  }

  decryptObject(cipherText: string): unknown {
    try {
      const key = CryptoJS.enc.Utf8.parse(this.encryptionKey);
      const iv = CryptoJS.enc.Utf8.parse(this.initializationVector);
      const ciph = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Hex.parse(cipherText),
      });
      const decrypted = CryptoJS.AES.decrypt(ciph, key, {
        keySize: 128 / 8,
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(plaintext) || plaintext;
    } catch {
      return null;
    }
  }
}
